import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Box, CircularProgress, TextField, Button, InputAdornment,
  List, ListItem, ListItemText, IconButton, FormControl, InputLabel,
  Select, MenuItem, Collapse, Tooltip, Chip
} from '@mui/material';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import Pagination from '../../utils/Pagination.js';
import useActivityLogger from '../../hooks/useActivityLogger';
import DomainOnboardingForm from './DomainOnbordingForm.js';
import { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, ELLIPSIS, CONTROL_HEIGHT } from '../../styles/recruiterTheme';

const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Page wrapper: content sits on the dashboard's lavender canvas (the dashboard supplies PAGE_PADDING).
const Page = styled.div`
  width: 100%;
  min-width: 0;
  font-family: ${FONT_BODY};
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// Each section is its own white card.
const SectionCard = styled.div`
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  padding: ${p => p.$pad ?? 0}px;
  min-width: 0;
`;

// Page header block: title + subtitle left, actions right.
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

// Compact resume count shown next to the page title.
const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  background: ${COLORS.accentSoft};
  color: ${COLORS.primaryDark};
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// Top row inside the table card: hint (left, aligned with the table's first column) + search (right).
const CardToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px;
  border-bottom: 1px solid ${COLORS.border};
`;

const Hint = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  line-height: 1.5;
  color: ${COLORS.faint};
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  && {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
    min-width: 960px;
  }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    cursor: pointer;
    height: 64px;
    &:hover td { background: #F3EEF9 !important; }
    &:nth-child(4n+1) td, &:nth-child(4n+2) td { background: #FDFBFF; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    border-bottom: none !important;
    vertical-align: middle;
    overflow: hidden;
  }
`;

const CellText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const SubText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  font-size: ${TYPE.caption}px;
  font-weight: 400;
  line-height: 1.5;
  color: ${COLORS.muted};
`;

// Summary preview: two lines max; the expanded row shows the full text.
const Clamp2 = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  word-break: break-word;
`;

const ExpandedRow = styled(TableRow)`
  && { background: #FAF7FF; }
`;

const ExpandedCell = styled(TableCell)`
  && {
    padding: 0 !important;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

const SeparatorRow = styled(TableRow)`
  && {
    height: 0;
    td { padding: 0 !important; border-bottom: 1px solid #F0EAF9 !important; }
  }
`;

const SummaryBox = styled.div`
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.muted};
`;

const SummaryText = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: ${COLORS.ink};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    text-transform: none !important;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover { transform: translateY(-1px); box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important; }
    &.Mui-disabled { opacity: 0.55; color: #fff !important; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    transition: background 0.18s !important;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

const ResumeBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.caption}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 32px;
    min-width: 64px;
    padding: 0 12px !important;
    white-space: nowrap;
    border: 1.5px solid #C4B0E8 !important;
    &:hover { background: #EDE7F6 !important; }
  }
`;

const UploadZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 32px;
  border: 2px dashed #C4B0E8;
  border-radius: ${RADIUS.lg}px;
  background: linear-gradient(135deg, #FAF7FF, #F3EEF9);
  width: 100%;
  max-width: 560px;
  cursor: pointer;
  transition: all 0.22s ease;
  &:hover {
    border-color: #7C5CBF;
    background: linear-gradient(135deg, #F3EEF9, #EDE7F6);
  }
`;

const StateBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 16px;
  text-align: center;
`;

const stateText = { fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: COLORS.muted };

// ─── JD chip list: wraps, shows at most `max` chips + a "+N" chip ─────────────
const jdChipSx = {
  fontFamily: FONT_BODY,
  fontWeight: 700,
  fontSize: TYPE.overline,
  height: 24,
  borderRadius: `${RADIUS.pill}px`,
  maxWidth: '100%',
  background: '#EDE7F6',
  color: '#4F378B',
  border: 'none',
  whiteSpace: 'nowrap',
  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', px: 1 },
};

function ChipList({ items, max = 2 }) {
  if (!items || items.length === 0) return <span style={{ color: '#C4B0E8' }}>—</span>;
  const shown = items.slice(0, max);
  const rest = items.slice(max);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', minWidth: 0 }}>
      {shown.map((item, i) => (
        <Chip key={i} label={item} title={item} size="small" sx={jdChipSx} />
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.join(', ')} placement="top">
          <Chip label={`+${rest.length}`} size="small" sx={{ ...jdChipSx, background: '#F3EEF9', color: COLORS.muted }} />
        </Tooltip>
      )}
    </Box>
  );
}

// "28 Sep 2026"; falls back to the raw value if it can't be parsed.
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Single expandable resume row ────────────────────────────────────────────
function ResumeRow({ resume, onViewResume, onToggleStatus }) {
  const [open, setOpen] = useState(false);
  const [isActive, setIsActive] = useState(resume.isActive !== false);
  const [isToggling, setIsToggling] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  // Keep local status in sync when the parent list is refetched/updated
  useEffect(() => { setIsActive(resume.isActive !== false); }, [resume.isActive]);

  const handleView = async (e) => {
    e.stopPropagation();
    if (isOpening) return;
    setIsOpening(true);
    try {
      await onViewResume(resume.file_name);
    } finally {
      setIsOpening(false);
    }
  };

  const hasSummary = !!resume.resume_summary;
  const hasEmail = !!resume.email;

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (!hasEmail) return;
    const newStatus = e.target.value === 'active'; // 'active' → true, 'inactive' → false
    if (newStatus === isActive) return; // no change
    setIsToggling(true);
    try {
      await onToggleStatus(resume.email, newStatus);
      setIsActive(newStatus);
    } catch {
      // revert — state unchanged
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <>
      <BodyRow onClick={() => hasSummary && setOpen(o => !o)}>

        <BodyCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          {hasSummary ? (
            <Tooltip title={open ? "Collapse summary" : "View full summary"} placement="right">
              <IconButton
                size="small"
                sx={{ color: '#6750A4', p: '2px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
              >
                {open ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : (
            <Box component="span" sx={{ display: 'inline-block', width: 24, flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <CellText title={resume.name || undefined} style={{ fontWeight: 600, fontSize: TYPE.body, lineHeight: 1.5 }}>{resume.name || '—'}</CellText>
            {hasEmail ? (
              <SubText title={resume.email}>{resume.email}</SubText>
            ) : (
              <Tooltip title="No email found — status cannot be toggled without an email" placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box sx={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#FFB300', flexShrink: 0,
                    boxShadow: '0 0 0 2px #FFF8E1',
                  }} />
                  <span style={{
                    fontFamily: FONT_BODY, fontSize: TYPE.caption,
                    fontWeight: 600, color: '#E65100', fontStyle: 'italic'
                  }}>
                    No email
                  </span>
                </Box>
              </Tooltip>
            )}
            {resume.phone && (
              <SubText title={String(resume.phone)} style={{ fontSize: TYPE.overline, fontVariantNumeric: 'tabular-nums' }}>
                {resume.phone}
              </SubText>
            )}
          </Box>
          </Box>
        </BodyCell>

        <BodyCell><ChipList items={resume.jdIDs} max={2} /></BodyCell>
        <BodyCell>
          {(resume.companyNames || []).length
            ? <CellText title={resume.companyNames.join(', ')}>{resume.companyNames.join(', ')}</CellText>
            : <span style={{ color: '#C4B0E8' }}>—</span>}
        </BodyCell>
        <BodyCell>
          <ResumeBtn onClick={handleView} disabled={isOpening || !resume.file_name}>
            {isOpening ? <CircularProgress size={12} sx={{ color: '#6750A4' }} /> : '📄 View'}
          </ResumeBtn>
        </BodyCell>
        <BodyCell sx={{ maxWidth: 260, color: hasSummary ? '#8B7DB5 !important' : '#C4B0E8 !important', fontSize: `${TYPE.caption}px !important` }}>
          {hasSummary
            ? <Clamp2>{resume.resume_summary}</Clamp2>
            : '—'}
        </BodyCell>
        <BodyCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {fmtDate(resume.processed_timestamp)}
        </BodyCell>

        {/* ── Status Column ── */}
        <BodyCell onClick={e => e.stopPropagation()} sx={{ overflow: 'hidden' }}>
  {!hasEmail ? (
    <Tooltip title="Cannot change status — no email associated with this resume" placement="left">
      <Chip
        label="UNKNOWN"
        size="small"
        sx={{
          fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.overline, letterSpacing: '0.06em',
          height: 24, borderRadius: `${RADIUS.pill}px`,
          bgcolor: '#F5F5F5', color: '#9E9E9E',
          border: '1px solid #E0E0E0',
        }}
      />
    </Tooltip>
  ) : (
    <Tooltip title={isActive ? 'Click to mark Inactive' : 'Click to mark Active'} placement="top">
      <Select
        value={isActive ? 'active' : 'inactive'}
        disabled={isToggling}
        onChange={handleToggle}
        size="small"
        onClick={e => e.stopPropagation()}
        renderValue={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: isActive ? '#2D7D46' : '#B3261E',
            }} />
            <span style={{
              fontSize: TYPE.caption, fontFamily: FONT_BODY, fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {isToggling ? 'Saving…' : isActive ? 'Active' : 'Inactive'}
            </span>
          </Box>
        )}
        sx={{
          fontFamily: FONT_BODY,
          fontSize: TYPE.caption,
          fontWeight: 600,
          borderRadius: `${RADIUS.sm}px`,
          height: CONTROL_HEIGHT.sm,
          width: 112,
          background: isActive ? '#F0FFF4' : '#FFF5F5',
          color: isActive ? '#1E7E34' : '#B3261E',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isActive ? '#C3E6CB' : '#F5C6CB',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isActive ? '#2D7D46' : '#B3261E',
          },
          '&.Mui-disabled': { opacity: 0.6 },
        }}
      >
        <MenuItem value="active" sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#1E7E34' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#2D7D46' }} />
            Mark Active
          </Box>
        </MenuItem>
        <MenuItem value="inactive" sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#B3261E' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#B3261E' }} />
            Mark Inactive
          </Box>
        </MenuItem>
      </Select>
    </Tooltip>
  )}
</BodyCell>
      </BodyRow>

      <ExpandedRow>
        <ExpandedCell colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <SummaryBox>
              <SummaryLabel>📋 Full Summary</SummaryLabel>
              <SummaryText>{resume.resume_summary}</SummaryText>
            </SummaryBox>
          </Collapse>
        </ExpandedCell>
      </ExpandedRow>
      <SeparatorRow><td colSpan={7} /></SeparatorRow>
    </>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
function AllResumes({ email, selectedJD: parentSelectedJD, onSetSnackbar }) {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedJD, setSelectedJD] = useState(parentSelectedJD || null);
  const [showUpload, setShowUpload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const { log } = useActivityLogger(email, 'recruiter');
  const [domains, setDomains] = useState([]);
  const [domainsError, setDomainsError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const domainsApiUrl = process.env.REACT_APP_DOMAINS_API;


  const itemsPerPage = 10;
  const getpdfApiUrl = process.env.REACT_APP_GET_PDF_API;

  // Uses the new joined endpoint
  const fetchResumes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(process.env.REACT_APP_GET_ALL_RESUMES_WITH_JD_API);
      if (response.data.success) setResumes(response.data.resumes);
      else setError("Failed to fetch resumes.");
    } catch (err) {
      setError("Failed to fetch resumes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResumes(); }, []);

  const fetchDomains = useCallback(async () => {
    setDomainsError(null);
    try {
      const response = await axios.get(domainsApiUrl);
      setDomains(response.data.data || []);
    } catch (err) {
      console.error("Error fetching domains:", err);
      setDomains([]);
      setDomainsError("Failed to load domains.");
    }
  }, [domainsApiUrl]);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const handleCloseDomainForm = () => {
    setShowDomainForm(false);
    fetchDomains();
  };

  // Search also works across jdIDs and companyNames
  const filteredResumes = resumes.filter(resume => {
    const s = searchQuery.toLowerCase();
    return (
      resume.file_name?.toLowerCase().includes(s) ||
      resume.name?.toLowerCase().includes(s) ||
      resume.email?.toLowerCase().includes(s) ||
      String(resume.phone || '').includes(s) ||
      (resume.jdIDs || []).some(j => j?.toLowerCase().includes(s)) ||
      (resume.companyNames || []).some(c => c?.toLowerCase().includes(s))
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredResumes.length / itemsPerPage));
  const paginatedResumes = filteredResumes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleResumedisplay = async (resumePaths, jdId) => {
    try {
      const pathsArray = Array.isArray(resumePaths) ? resumePaths.map(obj => obj.path) : [resumePaths];
      for (const resumePath of pathsArray) {
        const params = new URLSearchParams({ fileName: resumePath });
        if (jdId) params.append('jdId', jdId);
        const response = await axios.get(
          `${getpdfApiUrl}?${params.toString()}`,
          { responseType: 'arraybuffer' }
        );
        if (response.status === 200) {
          const contentType = response.headers['content-type'];
          const blob = new Blob([response.data], { type: contentType });
          const url = URL.createObjectURL(blob);
          if (contentType === 'application/pdf') window.open(url, '_blank');
          else window.location.href = url;
        } else {
          onSetSnackbar?.('Failed to fetch resume.');
        }
      }
    } catch { onSetSnackbar?.('Failed to fetch resume.'); }
  };

  const handleToggleResumeStatus = async (email, newStatus) => {
    log(newStatus ? 'MARK_RESUME_ACTIVE' : 'MARK_RESUME_INACTIVE', {
      metadata: { email, newStatus }
    });
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_UPDATE_RESUME_FIELDS_API}/by-email`,
        { email, isActive: newStatus }
      );
      if (response.status === 200) {
        setResumes(prev =>
          prev.map(r => r.email === email ? { ...r, isActive: newStatus } : r)
        );
        onSetSnackbar?.(newStatus ? '✅ Marked Active' : '🔴 Marked Inactive');
      }
    } catch {
      onSetSnackbar?.('Failed to update status.');
      throw new Error('Failed');
    }
  };

  const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified}`;
  const resetFileInput = () => { if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    setSelectedFiles(prev => {
      const seen = new Set(prev.map(fileKey));
      const fresh = incoming.filter(f => {
        const k = fileKey(f);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      return [...prev, ...fresh];
    });
    resetFileInput(); // allow re-selecting the same file after removal
  };
  const handleRemoveFile = (index, e) => {
    e.stopPropagation();
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    resetFileInput();
  };
  const clearSelectedFiles = () => { setSelectedFiles([]); resetFileInput(); };

  const handleResumeSubmit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (uploading) return;
    if (selectedFiles.length === 0) { onSetSnackbar?.('Please select files before submitting.'); return; }
    if (!selectedJD && !selectedDomain) { onSetSnackbar?.('Please select a domain before submitting.'); return; }
    log('UPLOAD_RESUMES', {
      jdId:       selectedJD?._id,
      targetName: selectedJD?.jobRole,
      metadata: {
        fileCount:  selectedFiles.length,
        fileNames:  selectedFiles.map(f => f.name),
        jdID:       selectedJD?.jdID,
        domain:     selectedDomain || null,
        uploadType: selectedJD ? 'jd_specific' : 'domain',
      }
    });
    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('files', file));
    let apiUrl = process.env.REACT_APP_RECRUITER_RESUME_UPLOAD_API;
    let isSearchAPI = false;
    if (selectedJD) {
      apiUrl = process.env.REACT_APP_RESUME_UPLOAD_AND_SEARCH_API; isSearchAPI = true;
      formData.append('jd._id', selectedJD._id);
      formData.append('job_description', selectedJD.jobDescription);
      formData.append('skills', selectedJD.skills);
      formData.append('experience', selectedJD.experience);
      formData.append('jdID', selectedJD.jdID);
      formData.append('job_role', selectedJD.jobRole);
      formData.append('recruiter_email', email);
    } else {
      formData.append('domain', selectedDomain);
    }
    setUploading(true);
    try {
      const response = await fetch(apiUrl, { method: "POST", body: formData, credentials: "include" });
      const d = await response.json().catch(() => ({}));
      if (response.ok) {
        onSetSnackbar?.(d.message);
        clearSelectedFiles(); setShowUpload(false); fetchResumes();
        if (isSearchAPI) { setSelectedJD(null); setSelectedDomain(""); }
      } else { onSetSnackbar?.(`Error: ${d.message || d.detail || response.statusText}`); }
    } catch { onSetSnackbar?.("Failed to upload files. Please try again."); }
    finally { setUploading(false); }
  };

  return (
    <>
      <GlobalStyle />

      {showDomainForm ? (
        <Page><SectionCard $pad={32}>
          <DomainOnboardingForm
           onBack={handleCloseDomainForm}
            onSuccess={fetchDomains}
            onSetSnackbar={onSetSnackbar}
          />
       </SectionCard></Page>
     ) : showUpload ? (
        <Page><SectionCard $pad={32}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => { setShowUpload(false); clearSelectedFiles(); }}
              sx={{ width: 36, height: 36, flexShrink: 0, background: '#F3EEF9', borderRadius: `${RADIUS.sm}px`, '&:hover': { background: '#EDE7F6' } }}>
              <ArrowBackIcon sx={{ color: '#4F378B' }} />
            </IconButton>
            <Box>
              <PageTitle>Upload Resumes</PageTitle>
              <PageSubtitle>Add candidates to the talent pool</PageSubtitle>
            </Box>
          </Box>

          {!selectedJD && (
          <FormControl size="small" sx={{ mb: 3, width: '100%', maxWidth: 560 }}>
            <InputLabel sx={{ fontFamily: FONT_BODY }}>Select Domain</InputLabel>
            <Select
              label="Select Domain"
              sx={{ height: 40, borderRadius: `${RADIUS.sm}px` }}
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
            >
              {domains.map(domain => (
                <MenuItem key={domain.slug} value={domain.slug}>
                  {domain.label}
                </MenuItem>
              ))}
            </Select>
            {domainsError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.error }}>{domainsError}</Typography>
                <GhostButton onClick={fetchDomains}>Retry</GhostButton>
              </Box>
            )}
          </FormControl>
        )}

          <UploadZone onClick={() => fileInputRef.current?.click()}>
            <CloudUploadIcon sx={{ fontSize: 64, color: '#7C5CBF' }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, lineHeight: 1.25, color: '#4F378B' }}>Click to upload resumes</Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.muted }}>Supports PDF, DOC, DOCX</Typography>
          </UploadZone>
          <input type="file" id="resumeFileInput" ref={fileInputRef} accept=".pdf,.doc,.docx" multiple hidden onChange={handleFileChange} />

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2, width: '100%', maxWidth: 560 }}>
              <List dense>
                {selectedFiles.map((file, index) => (
                  <ListItem key={index} sx={{ background: '#F8F4FF', borderRadius: `${RADIUS.sm}px`, mb: 1, pr: 6 }}
                    secondaryAction={<IconButton edge="end" onClick={(e) => handleRemoveFile(index, e)}><DeleteIcon sx={{ color: '#C2185B', fontSize: 18 }} /></IconButton>}>
                    <ListItemText primary={file.name}
                      primaryTypographyProps={{ title: file.name, style: { ...ELLIPSIS, display: 'block', fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: COLORS.ink } }} />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <GhostButton disabled={uploading} onClick={(e) => { e.preventDefault(); clearSelectedFiles(); }}>Clear All</GhostButton>
                <PrimaryButton
                  onClick={handleResumeSubmit}
                  disabled={uploading || (!selectedJD && !selectedDomain)}
                >
                  {uploading ? "Submitting…" : "Submit"}
                </PrimaryButton>
              </Box>
              {!selectedJD && !selectedDomain && (
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.error, mt: 1, textAlign: 'right' }}>
                  Select a domain to enable upload.
                </Typography>
              )}
            </Box>
          )}
        </SectionCard></Page>

      ) : (
        <Page>
          <PageHeader>
            <Box sx={{ minWidth: 0 }}>
              <TitleRow>
                <PageTitle>All Resumes</PageTitle>
                {!loading && !error && (
                  <CountPill title="Resumes matching the current search">
                    {filteredResumes.length} {filteredResumes.length === 1 ? 'resume' : 'resumes'}
                  </CountPill>
                )}
              </TitleRow>
              <PageSubtitle>Search and manage candidate resumes in your talent pool</PageSubtitle>
            </Box>
            <HeaderActions>
              <PrimaryButton onClick={() => setShowDomainForm(true)}>
                Add New Domain
              </PrimaryButton>
              <PrimaryButton onClick={() => setShowUpload(true)} startIcon={<CloudUploadIcon />}>
                Upload Resumes
              </PrimaryButton>
            </HeaderActions>
          </PageHeader>

          <SectionCard>
          <CardToolbar>
            <Hint>
              {!loading && !error && filteredResumes.length > 0 ? '💡 Click any row to expand the full summary' : ''}
            </Hint>
            <TextField
              placeholder="Search by name, email, JD ID, company…"
              size="small" value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              inputProps={{ 'aria-label': 'Search resumes' }}
              sx={{ width: 360, maxWidth: '100%', '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT.md, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8B7DB5", fontSize: 18 }} /></InputAdornment> }}
            />
          </CardToolbar>

          {loading && <StateBlock><CircularProgress sx={{ color: '#6750A4' }} /></StateBlock>}
          {error && !loading && (
            <StateBlock>
              <Typography sx={{ ...stateText, color: COLORS.error }}>{error}</Typography>
              <GhostButton onClick={fetchResumes}>Retry</GhostButton>
            </StateBlock>
          )}
          {filteredResumes.length === 0 && !loading && !error && (
            <StateBlock>
              <Typography sx={stateText}>No resumes available.</Typography>
            </StateBlock>
          )}

          {filteredResumes.length > 0 && !loading && !error && (
            <TableWrap>
            <StyledTable>
              <TableHead>
            <TableRow>
              {/* Candidate takes the remaining width; the rest are sized to their content. */}
              <HeadCell sx={{ pl: '44px !important' }}>Candidate</HeadCell>
              <HeadCell sx={{ width: 160 }}>JD IDs</HeadCell>
              <HeadCell sx={{ width: 120 }}>Company</HeadCell>
              <HeadCell sx={{ width: 104 }}>Resume</HeadCell>
              <HeadCell sx={{ width: 220 }}>Summary</HeadCell>
              <HeadCell sx={{ width: 116, textAlign: 'right' }}>Uploaded</HeadCell>
              <HeadCell sx={{ width: 144 }}>Status</HeadCell>
            </TableRow>
          </TableHead>
              <TableBody>
                {paginatedResumes.map((resume, index) => (
                  <ResumeRow key={resume.email || resume.file_name || resume._id || index} resume={resume} onViewResume={handleResumedisplay}   onToggleStatus={handleToggleResumeStatus} />
                ))}
              </TableBody>
            </StyledTable>
            </TableWrap>
          )}

          {filteredResumes.length > itemsPerPage && totalPages > 1 && (
            <Pagination itemsPerPage={itemsPerPage} currentPage={currentPage} totalPages={totalPages}
              handlePrevPage={() => setCurrentPage(p => Math.max(p - 1, 1))}
              handleNextPage={() => setCurrentPage(p => Math.min(p + 1, totalPages))} />
          )}
          </SectionCard>
        </Page>
      )}
    </>
  );
}

export default AllResumes;